import React from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import { previewIssueTexts } from "./terminationConfirmationDeliveryModel";

const ATTACHMENT_HINT = "Die Kündigungsbestätigung wird als PDF-Anhang versendet.";

function question(mode, recipient) {
  if (mode === "resend") {
    return `Die Kündigungsbestätigung wurde bereits versendet. Möchtest du sie erneut an ${recipient} senden?`;
  }
  if (mode === "retry") {
    return `Der letzte Versand ist fehlgeschlagen. Möchtest du die Kündigungsbestätigung erneut an ${recipient} senden?`;
  }
  return null;
}

/** One label/value row. Long addresses and subjects wrap inside the dialog
 *  instead of widening it past a phone screen. */
function Row({ label, value }) {
  return (
    <div className="grid grid-cols-1 gap-x-3 gap-y-0.5 sm:grid-cols-[9rem_minmax(0,1fr)]">
      <dt className="text-[12px] text-muted-foreground">{label}</dt>
      <dd className="m-0 min-w-0 break-all [overflow-wrap:anywhere] text-[13px] text-foreground">
        {value}
      </dd>
    </div>
  );
}

/**
 * KND-4b — "Kündigungsbestätigung per E-Mail senden" / "… erneut senden".
 *
 * PRESENTATIONAL. Every value shown is the server's preview — recipient,
 * sender, reply-to, subject, attachment name; nothing is derived here. When
 * the server says the version cannot be sent, the dialog explains why and
 * offers no send at all.
 *
 * PESSIMISTIC, like the withdrawal dialog: "Jetzt senden" does not close the
 * dialog by itself; while the request runs the dialog cannot be dismissed and
 * the button is disabled, so a double click is one request. The container
 * closes it once the server has answered.
 */
export default function TerminationConfirmationSendDialog({
  open,
  mode = "first",
  preview,
  busy = false,
  onOpenChange,
  onConfirm,
  onCloseAutoFocus,
}) {
  const sendable = preview?.sendable === true;
  const issues = sendable ? [] : previewIssueTexts(preview);
  const ask = sendable ? question(mode, preview.recipient) : null;
  const title = !sendable
    ? "Versand nicht möglich"
    : mode === "first"
      ? "Kündigungsbestätigung per E-Mail senden"
      : "Kündigungsbestätigung erneut senden";

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        onOpenChange(next);
      }}
    >
      <AlertDialogContent
        onCloseAutoFocus={onCloseAutoFocus}
        className="max-h-[90vh] overflow-y-auto"
        data-testid="termination-confirmation-send-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {/* The question names the address; an address has no spaces to wrap
              at, so it may break anywhere rather than widen the dialog. */}
          <AlertDialogDescription className="[overflow-wrap:anywhere]">
            {!sendable
              ? "Diese Version kann derzeit nicht per E-Mail versendet werden."
              : ask || ATTACHMENT_HINT}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {sendable ? (
          <>
            {ask ? (
              <p className="m-0 text-[13px] text-muted-foreground">{ATTACHMENT_HINT}</p>
            ) : null}
            <dl className="m-0 flex flex-col gap-2">
              <Row label="Empfänger" value={preview.recipient} />
              <Row label="Absender" value={preview.from_name} />
              <Row label="Absenderadresse" value={preview.from_email} />
              {preview.reply_to ? (
                <Row label="Antwort an" value={preview.reply_to} />
              ) : null}
              <Row label="Betreff" value={preview.subject} />
              <Row label="Anhang" value={preview.attachment_filename} />
            </dl>
          </>
        ) : (
          <ul className="m-0 list-disc pl-5 text-[13px] leading-snug text-foreground">
            {issues.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>
            {sendable ? "Abbrechen" : "Schließen"}
          </AlertDialogCancel>
          {sendable ? (
            <AlertDialogAction
              disabled={busy}
              aria-busy={busy}
              onClick={(event) => {
                event.preventDefault();
                onConfirm();
              }}
            >
              {busy ? "Wird gesendet …" : "Jetzt senden"}
            </AlertDialogAction>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
